import { ChangeDetectionStrategy, Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { getAnimationAppearOnTrigger } from '@examdojo/animation';
import { SyllabusQuery } from '@examdojo/category/v2';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { ToastService } from '@examdojo/core/toast';
import { ErrorHandlerService } from '@examdojo/error-handling';
import {
  SOURCE_STATUS_LIST,
  SOURCE_STATUS_TO_LABEL,
  SOURCE_TYPE_LIST,
  SOURCE_TYPE_TO_LABEL,
  SourceListItemHttpModel,
  SourceService,
  SourceStatus,
  SourceType,
} from '@examdojo/source';
import { LoaderComponent } from '@examdojo/ui/loader';
import {
  ColDef,
  FilterModel,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowClickedEvent,
  SortModelItem,
  ValueFormatterParams,
} from 'ag-grid-community';
import { EMPTY, distinctUntilChanged, finalize, first, tap } from 'rxjs';
import { CmsUrlParts } from '../../cms/cms.model';
import { AgGirdColumnStateManager } from '../../shared/ag-grid/ag-gird-column-state.manager';
import { SourceCreateDialogService } from './source-create-dialog.service';
import { SourceListStatusCellRendererComponent } from './source-list-status-cell-renderer.component';
import { SourceListService } from './source-list.service';

const SOURCE_LIST_VIEW_STATE_STORAGE_KEY = 'source-grid-state';

@Component({
  selector: 'exam-source-list',
  template: `
    <dojo-master-list-view
      [columnDefs]="columnDefs"
      [gridOptions]="gridOptions"
      [canAdd]="false"
      [showToolbarAddButton]="true"
      [hideSearch]="true"
      [sizeSearchFiled]="'s'"
      (add)="addSource()"
      [agGridAdditionalClass]="'small-height'"
      (gridReady)="onGridReady($event)"
      (gridRowClicked)="openSource($event)"
      [@appearOnTrigger]="!isLoading()"
    />

    @if (isLoading()) {
      <div class="absolute left-0 top-0 flex h-full w-full items-center justify-center">
        <dojo-loader />
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MasterListViewComponent, LoaderComponent],
  providers: [provideSimpleListView(SourceListService)],
  animations: [getAnimationAppearOnTrigger()],
})
export class SourceListComponent {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly router: Router,
    private readonly sourceService: SourceService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly destroyRef: DestroyRef,
    private readonly toastService: ToastService,
    private readonly sourceCreateDialogService: SourceCreateDialogService,
    private readonly sourceListService: SourceListService,
    private readonly syllabusQuery: SyllabusQuery,
  ) {
    this.syllabusQuery.active$
      .pipe(
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        tap(() => {
          // Only refresh if the grid is initialized and component is active
          const api = this.gridApi();
          if (api && !this.isLoading()) {
            api.refreshServerSide();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  readonly gridApi = signal<GridApi<SourceListItemHttpModel> | null>(null);
  readonly isLoading = signal<boolean>(true);

  readonly columnDefs: Array<ColDef<SourceListItemHttpModel>> = [
    {
      field: 'name',
      headerName: 'Name',
      valueGetter: ({ data }) => {
        return data?.name ?? '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'source_type',
      headerName: 'Source Type',
      valueGetter: ({ data }) => {
        if (!data?.source_type) {
          return '';
        }
        return SOURCE_TYPE_TO_LABEL[data.source_type as SourceType];
      },
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      filterParams: {
        values: SOURCE_TYPE_LIST,
        valueFormatter: (params: ValueFormatterParams<SourceListItemHttpModel, SourceType>) =>
          SOURCE_TYPE_TO_LABEL[params.value as SourceType],
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      cellRenderer: SourceListStatusCellRendererComponent,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      filterParams: {
        values: SOURCE_STATUS_LIST,
        valueFormatter: (params: ValueFormatterParams<SourceListItemHttpModel, SourceStatus>) =>
          SOURCE_STATUS_TO_LABEL[params.value as SourceStatus],
      },
    },
    {
      field: 'tier',
      headerName: 'Tier',
      valueGetter: ({ data }) => {
        return data?.tier?.toString() ?? '';
      },
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [0, 1, 2, 3, 4, 5],
        valueFormatter: (params: ValueFormatterParams<SourceListItemHttpModel, number>) =>
          params.value?.toString() ?? '',
      },
    },
    {
      field: 'user_email',
      headerName: 'Author',
      valueGetter: ({ data }) => {
        return data?.user_email ?? '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      valueFormatter: ({ data }) => {
        if (!data?.created_at) {
          return '';
        }
        return this.dateTimeService.formatDateRelativeToNow(data.created_at, true);
      },
      tooltipValueGetter: ({ data }) => {
        if (!data?.created_at) {
          return '';
        }
        return this.dateTimeService.formatDate(data.created_at, DateTimeFormat.ShortDateTime);
      },
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      valueFormatter: ({ data }) => {
        if (!data?.updated_at) {
          return '';
        }
        return this.dateTimeService.formatDateRelativeToNow(data.updated_at, true);
      },
      tooltipValueGetter: ({ data }) => {
        if (!data?.updated_at) {
          return '';
        }
        return this.dateTimeService.formatDate(data.updated_at, DateTimeFormat.ShortDateTime);
      },
    },
  ];

  readonly serverSideDatasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      const { startRow, endRow, sortModel, filterModel } = params.request;
      const sortBy: SortModelItem[] = sortModel.length ? sortModel : [{ colId: 'created_at', sort: 'desc' }];

      this.sourceService
        .fetchPaginatedListView({
          startRow,
          endRow: endRow ? endRow - 1 : undefined,
          sortBy,
          filterBy: {
            ...filterModel,
            syllabus_id: {
              filterType: 'set',
              values: [this.syllabusQuery.getActiveId()!],
            },
          } as FilterModel,
        })
        .pipe(
          first(),
          tap({
            next: (data) => {
              params.success({ rowData: data.data, rowCount: data.totalCount });
              this.columnStateManager.initialRowsLoaded.set(true);
            },
            error: () => {
              params.fail();
            },
          }),
          this.errorHandlerService.catchError(
            `[SourceListComponent.getRows]: failed to load the sources`,
            () => EMPTY,
            { toast: `Could not load sources` },
          ),
          finalize(() => this.isLoading.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    },
  };

  readonly gridOptions: GridOptions<SourceListItemHttpModel> = {
    getRowId: (params) => params.data?.id ?? '',
    pagination: true,
    paginationAutoPageSize: true,
    cacheBlockSize: 100,
    rowModelType: 'serverSide',
    serverSideDatasource: this.serverSideDatasource,
    suppressCellFocus: true,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No sources found',
      body: 'Please try with another filter',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      menuTabs: [],
    },
    context: {
      componentParent: this,
    },
    animateRows: true,
    rowSelection: {
      mode: 'singleRow',
      checkboxes: false,
    },
    getContextMenuItems: (event) => [
      {
        name: 'Open in New Tab',
        icon: `<i class="fa-light fa-arrow-up-right-from-square"></i>`,
        action: () => {
          if (!event.node?.data) {
            return;
          }

          window.open(`/${CmsUrlParts.Sources}/${event.node?.data.id}`, '_blank', 'noopener,noreferrer');
        },
      },
    ],
    rowClass: 'cursor-pointer',
    onPaginationChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onSortChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onFilterChanged: () => {
      this.columnStateManager.saveGridState();
    },
  };

  readonly columnStateManager = new AgGirdColumnStateManager(this.gridOptions, SOURCE_LIST_VIEW_STATE_STORAGE_KEY);

  // TODO: Remove once deleting is allowed on the backend.
  readonly isDeletingAllowed = signal(false);

  async addSource() {
    const sourceId = await this.sourceCreateDialogService.openDialog();

    if (!sourceId) {
      return;
    }

    const activeSyllabus = this.syllabusQuery.getActive();
    if (!activeSyllabus) {
      return;
    }

    this.router.navigate(['/', CmsUrlParts.Syllabus, activeSyllabus.slug, CmsUrlParts.Sources, sourceId]);
  }

  openSource(event: RowClickedEvent<SourceListItemHttpModel>) {
    if (!event.data) {
      return;
    }

    const activeSyllabus = this.syllabusQuery.getActive();
    if (!activeSyllabus) {
      return;
    }

    this.router.navigate(['/', CmsUrlParts.Syllabus, activeSyllabus.slug, CmsUrlParts.Sources, event.data.id]);
  }

  onGridReady(event: GridReadyEvent<SourceListItemHttpModel>) {
    this.gridApi.set(event.api);

    this.columnStateManager.gridApi.set(event.api);
    this.columnStateManager.gridReady.set(true);
    this.columnStateManager.loadGridState();
  }
}
